import React, { useState, useEffect, useRef } from "react";
import { debounce } from "lodash";

const SearchableDropdown = ({
    label = "",
    fetchFunction,
    onSelect,
    search_box_placeholder = "Type to search...",
    placeholder = "No option selected",
    valueKey = "",
    labelKey = "name",
    subLabelKey = "",
    showSubLabel = false,
    width = "full",
    disabled = false,
    includeAllOption = false,
    allOptionLabel = "All",
    value = null,
    multiple = false,
}) => {
    const [searchTerm, setSearchTerm] = useState("");
    const [isOpen, setIsOpen] = useState(false);
    const [options, setOptions] = useState([]);
    const [selectedOption, setSelectedOption] = useState(value);
    const [loading, setLoading] = useState(false);

    const dropdownRef = useRef(null);
    useEffect(() => {
        setSelectedOption(value);
    }, [value]);

    if (!fetchFunction) {
        throw new Error("fetchFunction prop is required for SearchableDropdown");
    }

    // Debounced fetch
    const debouncedFetch = debounce(async (term) => {
        if (disabled) return; // 🔹 Prevent fetching when disabled
        setLoading(true);
        const data = await fetchFunction({ search: term });
        let finalData = Array.isArray(data) ? data : data?.data || [];
        const hasResults = finalData.length > 0;
        if (includeAllOption && hasResults) {
            const allOption = valueKey
                ? { [valueKey]: null, [labelKey]: allOptionLabel }
                : { [labelKey]: allOptionLabel };
            finalData = [allOption, ...finalData];
        }
        setOptions(finalData);
        setLoading(false);
    }, 300);

    useEffect(() => {
        if (isOpen && !disabled) debouncedFetch(searchTerm);
        return debouncedFetch.cancel;
    }, [searchTerm, isOpen, disabled]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleSelect = (option) => {
        if (disabled) return;

        if (multiple) {
            const current = Array.isArray(selectedOption) ? selectedOption : [];
            const isSelected = current.some(
                (item) => item[valueKey] === option[valueKey],
            );
            const updated = isSelected
                ? current.filter((item) => item[valueKey] !== option[valueKey])
                : [...current, option];
            setSelectedOption(updated);
            if (onSelect) onSelect(updated);
            return;
        }

        setSelectedOption(option);
        setIsOpen(false);
        setSearchTerm("");
        if (onSelect) onSelect(option);
    };

    const displayLabel = () => {
        if (multiple) {
            const selected = Array.isArray(selectedOption) ? selectedOption : [];
            if (selected.length === 0) return placeholder;
            if (selected.length === 1) return selected[0][labelKey];
            return `${selected.length} selected`;
        }
        return selectedOption ? selectedOption[labelKey] : placeholder;
    };

    return (
        <div ref={dropdownRef} className={`relative w-${width}`}>
            {label && (
                <label
                    className={`block text-sm font-medium mb-1 ${disabled ? "text-gray-400" : "text-gray-700"
                        }`}
                >
                    {label}
                </label>
            )}

            <div
                className={`border rounded-lg px-3 py-2 flex justify-between items-center ${disabled
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-white text-gray-700 cursor-pointer"
                    }`}
                onClick={() => !disabled && setIsOpen(!isOpen)}
            >
                <span>{displayLabel()}</span>
                <svg
                    className={`w-5 h-5 text-gray-500 transform transition-transform duration-200 ${isOpen ? "rotate-180" : ""
                        } ${disabled ? "opacity-50" : ""}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M19 9l-7 7-7-7"
                    />
                </svg>
            </div>

            {isOpen && !disabled && (
                <div className="absolute mt-1 w-full bg-white border rounded-lg shadow-lg z-50">
                    <input
                        type="text"
                        placeholder={search_box_placeholder}
                        className="w-full px-3 py-2 border-b outline-none text-sm"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />

                    <div className="max-h-40 min-h-[7vh] overflow-y-auto">
                        {loading ? (
                            <div className="h-full w-full min-h-[7vh] flex justify-center items-center">
                                <div className="animate-spin border-t h-5 w-5 rounded-full border-blue-600"></div>
                            </div>
                        ) : options.length > 0 ? (
                            options.map((option, index) => {
                                const isChecked =
                                    multiple &&
                                    Array.isArray(selectedOption) &&
                                    selectedOption.some(
                                        (item) => item[valueKey] === option[valueKey],
                                    );
                                return (
                                    <div
                                        key={index}
                                        className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm text-gray-700 flex items-center gap-2"
                                        onClick={() => handleSelect(option)}
                                    >
                                        {multiple && (
                                            <input
                                                type="checkbox"
                                                checked={!!isChecked}
                                                readOnly
                                                className="pointer-events-none"
                                            />
                                        )}
                                        <div className="font-medium">
                                            {option[labelKey]}
                                            {showSubLabel && subLabelKey && option[subLabelKey] && (
                                                <span className="text-gray-400 text-xs"> ({option[subLabelKey]})</span>
                                            )}
                                        </div>
                                    </div>
                                );
                            })
                        ) : (
                            <div className="px-3 py-2 text-sm text-gray-500">
                                No results found
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default SearchableDropdown;
